
import React, { createContext, useState, useContext, useEffect } from "react";
import { AccountInfo } from "@azure/msal-browser";
import { msalInstance, tokenRequest } from "../msalAuth";
import * as msalAuth from "../msalAuth"; // Import all exports from msalAuth
import { getSP, initializeSP } from "../services/pnpjsConfig";
import { useSharePoint } from "@/context/SharePointContext";

// This is a SharePoint-specific auth provider, not the main app auth
interface SharePointAuthContextType {
  isAuthenticated: boolean;
  isAuthenticating: boolean;
  authError: string | null;
  initializeAuth: () => Promise<void>;
  logout: () => Promise<void>;
}

const SharePointAuthContext = createContext<SharePointAuthContextType>({
  isAuthenticated: false,
  isAuthenticating: false,
  authError: null,
  initializeAuth: async () => {},
  logout: async () => {}
});

export const useSharePointAuth = () => useContext(SharePointAuthContext);

export const SharePointAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isAuthenticating, setIsAuthenticating] = useState<boolean>(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const { siteUrl } = useSharePoint();

  const initializeAuth = async (): Promise<void> => {
    setIsAuthenticating(true);
    setAuthError(null);

    try {
      // Get all accounts and check if we have any
      msalAuth.setActiveAccountIfNeeded();
      const accounts = msalInstance.getAllAccounts();
      let account: AccountInfo | null = null;
      
      if (accounts.length > 0) {
        // Use the first account
        account = accounts[0];
        // Explicitly set this account as active
        msalInstance.setActiveAccount(account);
      } else {
        // No account found, initiate login via popup
        const loginResponse = await msalInstance.loginPopup(tokenRequest);
        account = loginResponse.account;
        // Set the newly logged in account as active
        msalInstance.setActiveAccount(account);
      }
      
      // Verify we have an active account
      if (!msalInstance.getActiveAccount()) {
        throw new Error("Failed to set active account");
      }
      
      // If site URL is available, initialize SP with it
      if (siteUrl) {
        initializeSP(siteUrl);
      }
      
      // Now, acquire a token silently with the account explicitly included
      try {
        await msalInstance.acquireTokenSilent({
          ...tokenRequest,
          account: msalInstance.getActiveAccount() || undefined
        });
      } catch (error) {
        console.warn("Silent token acquisition failed, fallback to interactive", error);
        const popupResponse = await msalInstance.acquireTokenPopup({
          ...tokenRequest,
          account: msalInstance.getActiveAccount() || undefined
        });
        
        // Ensure we update the active account from the response
        if (popupResponse.account) {
          msalInstance.setActiveAccount(popupResponse.account);
        }
      }
      
      // Once the token is acquired, initialize PnPjs
      await getSP();
      
      setIsAuthenticated(true);
      localStorage.setItem("sharepoint_authenticated", "true");
    } catch (error) {
      console.error("SharePoint authentication error:", error);
      setAuthError(error instanceof Error ? error.message : "Unknown authentication error");
      setIsAuthenticated(false);
      localStorage.removeItem("sharepoint_authenticated");
      
      // If silent token acquisition fails, try interactive as fallback
      try {
        if (error instanceof Error && 
            (error.message.includes("no_account_error") || 
             error.message.includes("interaction_required"))) {
          
          const result = await msalInstance.acquireTokenPopup(tokenRequest);
          if (result) {
            // Set the account from the result as active
            msalInstance.setActiveAccount(result.account);
            
            // If site URL is available, initialize SP with it
            if (siteUrl) {
              initializeSP(siteUrl);
            }
            
            // Initialize PnPjs after successful authentication
            await getSP();
            setIsAuthenticated(true);
            localStorage.setItem("sharepoint_authenticated", "true");
            // Clear the error since we recovered
            setAuthError(null);
          }
        }
      } catch (fallbackError) {
        console.error("Interactive authentication fallback failed:", fallbackError);
      }
    } finally {
      setIsAuthenticating(false);
    }
  };

  const logout = async (): Promise<void> => {
    try {
      // Use logoutPopup instead of redirect for consistency
      await msalInstance.logoutPopup();
    } catch (error) {
      console.error("Logout error:", error);
    }
    setIsAuthenticated(false);
    localStorage.removeItem("sharepoint_authenticated");
  };

  // Attempt silent authentication if previously authenticated.
  useEffect(() => {
    const wasAuthenticated = localStorage.getItem("sharepoint_authenticated") === "true";
    if (wasAuthenticated) {
      initializeAuth().catch((error) =>
        console.error("Silent authentication failed:", error)
      );
    }
  }, []);

  return (
    <SharePointAuthContext.Provider
      value={{
        isAuthenticated,
        isAuthenticating,
        authError,
        initializeAuth,
        logout,
      }}
    >
      {children}
    </SharePointAuthContext.Provider>
  );
};

// Rename the exported component for clarity
export const AuthProvider = SharePointAuthProvider;
